import { isLoaded } from "expo-font";
import React, { forwardRef } from "react";
import {
  Linking,
  StyleSheet,
  Text as NativeText,
  TextInput as NativeTextInput,
  type GestureResponderEvent,
  type TextProps,
  type TextInputProps,
  type TextStyle,
} from "react-native";
import { text, typography } from "@/src/theme/tokens";
import { linkifyPostText } from "@/src/lib/postLinks";

function fontStyle(content: unknown, style: TextStyle = {}): TextStyle {
  const arabic = /[\u0600-\u06ff]/.test(typeof content === "string" ? content : Array.isArray(content) ? content.filter(x => typeof x === "string").join("") : "");
  const weight = style.fontWeight === "bold" ? 700 : Number(style.fontWeight) || 400;
  const family = arabic
    ? weight >= 800 ? typography.family.arabicExtrabold : weight >= 600 ? typography.family.arabicBold : weight >= 500 ? typography.family.arabicMedium : typography.family.arabicRegular
    : weight >= 800 ? typography.family.extrabold : weight >= 700 ? typography.family.bold : weight >= 600 ? typography.family.semibold : weight >= 500 ? typography.family.medium : typography.family.regular;
  // An unavailable font must retain the native fallback, including on font-load failure.
  return { ...(isLoaded(family) ? { fontFamily: family, fontWeight: "normal" } : {}), ...(arabic ? { writingDirection: "rtl" } : {}) };
}

export function Text({ style, children, ...props }: TextProps) {
  const shouldAutoLink = typeof children === "string" && !props.onPress;
  const segments = shouldAutoLink ? linkifyPostText(children) : null;
  const hasLinks = segments?.some((segment) => segment.kind === "link") ?? false;

  const renderedChildren = hasLinks
    ? segments!.map((segment, index) =>
        segment.kind === "link" ? (
          <NativeText
            key={`${segment.url}-${index}`}
            accessibilityRole="link"
            onPress={(event: GestureResponderEvent) => {
              event.stopPropagation?.();
              void Linking.openURL(segment.url).catch((error) => {
                if (__DEV__) console.warn("[text] link open failed", error);
              });
            }}
            style={{ color: text.brand, textDecorationLine: "underline" }}
          >
            {segment.value}
          </NativeText>
        ) : (
          <React.Fragment key={`text-${index}`}>{segment.value}</React.Fragment>
        )
      )
    : children;

  return (
    <NativeText
      {...props}
      style={[{ color: text.primary }, style, fontStyle(children, StyleSheet.flatten(style))]}
    >
      {renderedChildren}
    </NativeText>
  );
}

export const TextInput = forwardRef<NativeTextInput, TextInputProps>(function BrandTextInput({ style, ...props }, ref) {
  return <NativeTextInput ref={ref} placeholderTextColor={text.muted} selectionColor={text.brand} {...props}
    style={[{ color: text.primary }, style, fontStyle(props.value ?? props.defaultValue ?? props.placeholder, StyleSheet.flatten(style))]} />;
});
